/**
 * importConfigs
 *
 * Pre-defined AppField configurations for every importable entity.
 * Used by EntityImportButton / ExcelImportModal to configure the column-mapping
 * step without requiring each callsite to define schema by hand.
 *
 * Also exports a default validateRow function per entity for preview-step validation.
 *
 * buildItemsImportConfig(customFields) returns a dynamic config that merges
 * the static item fields with the workspace's enabled custom fields so any CSV
 * column can be mapped to a custom field without a code change.
 */

import type { AppField } from '../components/prioritization/import-export/ColumnMapper';
import type { Attribute } from '../types/prioritization';

export type ImportEntityType =
  | 'items'
  | 'outcome_drivers'
  | 'backplan_nodes'
  | 'roadmap_nodes'
  | 'open_questions';

export interface ImportConfig {
  entityType: ImportEntityType;
  /** Human-readable label for the modal title */
  label: string;
  /** Human-readable description for the modal header */
  description: string;
  /** Field definitions for the column-mapping step */
  appFields: AppField[];
  /** Optional row-level validator */
  validateRow?: (row: Record<string, unknown>) => {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  };
}

// ── Items ─────────────────────────────────────────────────────────────────────

export const itemsImportConfig: ImportConfig = {
  entityType: 'items',
  label: 'Import items',
  description: 'Upload a CSV or Excel file to bulk-import items into the workspace.',
  appFields: [
    { key: 'title', label: 'Title', required: true, type: 'string' },
    { key: 'description', label: 'Description', required: false, type: 'string' },
    // Metadata fields — used for Miro color mappings (value-based rules)
    { key: 'altitudeLevel', label: 'Altitude Level (1–5)', required: false, type: 'string' },
    { key: 'itemCategory', label: 'Item Category', required: false, type: 'string' },
    { key: 'metaCategory', label: 'Meta Category', required: false, type: 'string' },
    { key: 'strategicBucket', label: 'Strategic Bucket', required: false, type: 'string' },
    { key: 'source', label: 'Source', required: false, type: 'string' },
    { key: 'tags', label: 'Tags', required: false, type: 'string' },
    { key: 'desiredOutcome', label: 'Desired Outcome', required: false, type: 'string' },
    { key: 'beneficiary', label: 'Beneficiary', required: false, type: 'string' },
    { key: 'evidenceOfSuccess', label: 'Evidence of Success', required: false, type: 'string' },
    { key: 'whyNow', label: 'Why Now', required: false, type: 'string' },
    { key: 'obstacles', label: 'Obstacles', required: false, type: 'string' },
    { key: 'work_type', label: 'Work Type', required: false, type: 'string' },
    { key: 'launch_type', label: 'Launch Type', required: false, type: 'string' },
    { key: 'investment_type', label: 'Investment Type', required: false, type: 'string' },
    { key: 'exit_status', label: 'Exit Status', required: false, type: 'string' },
    { key: 'parentItemId', label: 'Parent Item ID', required: false, type: 'string' },
    { key: 'external_id', label: 'External ID', required: false, type: 'string',
      description: 'Stable upstream key — re-imports upsert instead of duplicating' },
    { key: 'id', label: 'Item ID (UUID)', required: false, type: 'string',
      description: 'Existing item UUID for partial update' },
  ],
  validateRow(row) {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!row.title || String(row.title).trim() === '') {
      errors.push('Title is required');
    }
    return { isValid: errors.length === 0, errors, warnings };
  },
};

// ── Outcome Drivers ───────────────────────────────────────────────────────────

export const outcomeDriversImportConfig: ImportConfig = {
  entityType: 'outcome_drivers',
  label: 'Import Outcome Drivers',
  description: 'Upload a CSV or Excel file to import outcome driver artifacts.',
  appFields: [
    { key: 'title', label: 'Title', required: true, type: 'string' },
    { key: 'context_of_purpose', label: 'Context of Purpose', required: false, type: 'string' },
    { key: 'current_state', label: 'Current State', required: false, type: 'string' },
    { key: 'problem_opportunity', label: 'Problem / Opportunity', required: false, type: 'string' },
    { key: 'ramifications', label: 'Ramifications', required: false, type: 'string' },
    { key: 'narrative', label: 'Narrative', required: false, type: 'string' },
  ],
  validateRow(row) {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!row.title || String(row.title).trim() === '') {
      errors.push('Title is required');
    }
    return { isValid: errors.length === 0, errors, warnings };
  },
};

// ── Backplan Nodes ────────────────────────────────────────────────────────────

export const backplanNodesImportConfig: ImportConfig = {
  entityType: 'backplan_nodes',
  label: 'Import Backplan Nodes',
  description: 'Upload a CSV or Excel file to import outcome condition nodes into a back plan.',
  appFields: [
    { key: 'text', label: 'Node Text', required: true, type: 'string' },
    { key: 'tense', label: 'Tense (past / present / future)', required: false, type: 'string' },
    { key: 'is_goal', label: 'Is Goal? (true/false)', required: false, type: 'boolean' },
    { key: 'status', label: 'Status', required: false, type: 'string' },
    { key: 'tags', label: 'Tags', required: false, type: 'string' },
    { key: 'position', label: 'Position', required: false, type: 'number' },
  ],
  validateRow(row) {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!row.text || String(row.text).trim() === '') {
      errors.push('Node text is required');
    }
    const validTenses = ['past', 'present', 'future'];
    if (row.tense && !validTenses.includes(String(row.tense))) {
      warnings.push(`Tense "${row.tense}" is not recognized; will default to "future"`);
    }
    return { isValid: errors.length === 0, errors, warnings };
  },
};

// ── Roadmap Nodes (Planning) ─────────────────────────────────────────────────

export const roadmapNodesImportConfig: ImportConfig = {
  entityType: 'roadmap_nodes',
  label: 'Import Roadmap Nodes',
  description: 'Upload a CSV or Excel file to import outcome condition nodes into a roadmap.',
  appFields: [
    { key: 'text', label: 'Node Text', required: true, type: 'string' },
    { key: 'tense', label: 'Tense (past / present / future)', required: false, type: 'string' },
    { key: 'is_goal', label: 'Is Goal? (true/false)', required: false, type: 'boolean' },
    { key: 'status', label: 'Status', required: false, type: 'string' },
    { key: 'tags', label: 'Tags', required: false, type: 'string' },
    { key: 'position', label: 'Position', required: false, type: 'number' },
  ],
  validateRow(row) {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!row.text || String(row.text).trim() === '') {
      errors.push('Node text is required');
    }
    const validTenses = ['past', 'present', 'future'];
    if (row.tense && !validTenses.includes(String(row.tense))) {
      warnings.push(`Tense "${row.tense}" is not recognized; will default to "future"`);
    }
    return { isValid: errors.length === 0, errors, warnings };
  },
};

// ── Open Questions ────────────────────────────────────────────────────────────

export const openQuestionsImportConfig: ImportConfig = {
  entityType: 'open_questions',
  label: 'Import Open Questions',
  description: 'Upload a CSV or Excel file to import open questions into the workspace log.',
  appFields: [
    { key: 'text',           label: 'Question',                  required: true,  type: 'string' },
    { key: 'currentTheory',  label: 'Current Theory',            required: false, type: 'string' },
    { key: 'sourceOfAnswer', label: 'Source of Answer',          required: false, type: 'string' },
    { key: 'stepsToAnswer',  label: 'Source of a Better Answer', required: false, type: 'string' },
    { key: 'status',         label: 'Status',                    required: false, type: 'string',
      description: 'open · in_progress · resolved · deferred' },
  ],
  validateRow(row) {
    const errors: string[] = [];
    const warnings: string[] = [];
    if (!row.text || String(row.text).trim() === '') {
      errors.push('Question text is required');
    }
    const validStatuses = ['open', 'in_progress', 'resolved', 'deferred'];
    if (row.status && !validStatuses.includes(String(row.status))) {
      warnings.push(`Status "${row.status}" is not recognized; will default to "open"`);
    }
    return { isValid: errors.length === 0, errors, warnings };
  },
};

// ── Registry ──────────────────────────────────────────────────────────────────

export const IMPORT_CONFIGS: Record<ImportEntityType, ImportConfig> = {
  items: itemsImportConfig,
  outcome_drivers: outcomeDriversImportConfig,
  backplan_nodes: backplanNodesImportConfig,
  roadmap_nodes: roadmapNodesImportConfig,
  open_questions: openQuestionsImportConfig,
};

// ── Dynamic factory (custom fields) ──────────────────────────────────────────

/**
 * Map an Attribute type to the AppField type expected by the column mapper.
 * Custom field values will be routed into items.metadata by the modal before
 * calling onImport — callers do not need to handle this themselves.
 */
function attributeToAppField(attr: Attribute): AppField {
  const type =
    attr.type === 'number' ? 'number' :
    attr.type === 'boolean' ? 'boolean' :
    attr.type === 'date' ? 'date' :
    'string';

  return {
    key: attr.key,
    label: attr.label,
    required: false,
    type,
    isCustomField: true,
    description: attr.description,
  };
}

/**
 * Returns an ImportConfig for items that includes the workspace's enabled
 * custom fields merged into the appFields list.
 *
 * Usage:
 *   const config = buildItemsImportConfig(attributesStore.getEnabledCustomFields('item'));
 */
export function buildItemsImportConfig(customFields: Attribute[]): ImportConfig {
  const customAppFields = customFields.map(attributeToAppField);

  return {
    ...itemsImportConfig,
    appFields: [...itemsImportConfig.appFields, ...customAppFields],
  };
}

/**
 * Restructure a mapped row so that any custom field values are nested inside
 * a `metadata` object, matching the DB schema for items.
 *
 * Called by ExcelImportModal before invoking onImport, using the set of
 * custom field keys derived from the active appFields list.
 */
export function restructureCustomFieldRows(
  rows: Record<string, unknown>[],
  customFieldKeys: Set<string>
): Record<string, unknown>[] {
  if (customFieldKeys.size === 0) return rows;

  return rows.map(row => {
    const base: Record<string, unknown> = {};
    const meta: Record<string, unknown> = {};

    for (const [k, v] of Object.entries(row)) {
      if (customFieldKeys.has(k)) {
        meta[k] = v;
      } else {
        base[k] = v;
      }
    }

    if (Object.keys(meta).length > 0) {
      const existing = (base.metadata ?? {}) as Record<string, unknown>;
      base.metadata = { ...existing, ...meta };
    }

    return base;
  });
}
